import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  Plus, 
  Search, 
  Filter, 
  Image as ImageIcon, 
  Headphones, 
  MoreHorizontal, 
  Eye, 
  Edit2, 
  Trash2, 
  CheckCircle, 
  Calendar, 
  Tag, 
  Volume2, 
  X, 
  Check,
  ArrowLeft,
  Database,
  AlertTriangle,
  AlertCircle,
  Upload
} from "lucide-react";
import { questionService, normalizeCompetency, type Question } from "@/services/questionService";
import { mediaService } from "@/services/mediaService";
import { supabase } from "@/lib/supabaseClient";
import { getCompetencyLabel } from "@/data/tefScoreScale";
import { normalizeListeningSection } from "@/data/tefStructure";
import { getQuestionAudioUrl, getOptionImageUrl, normalizeQuestionCompetency, safeParseObject, normalizeQuestionOptions } from "@/utils/questionNormalizer";
import { AudioPlayerWithSpeed } from "@/components/audio/AudioPlayerWithSpeed";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { ContextModal } from "@/components/ui/ContextModal";

function shouldRenderImageOptions(question: any): boolean {
  if (!question) return false;
  
  const normalize = (str: any) => 
    String(str || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  const competence = normalize(question.competence || question.competency);
  const section = normalize(question.section || question.sectionKey || question.audioType || question.subType || "");

  const isCO = competence.includes("comprehension orale") || competence === "co" || competence === "listening";
  const isImageSection = section.includes("dialogues associes a des images") || section === "section_a";

  if (isCO && isImageSection) return true;
  if (question.requiresOptionImages === true) return true;
  if (question.optionAImage || question.optionBImage || question.optionCImage || question.optionDImage) return true;
  
  if (Array.isArray(question.options) && question.options.some((o: any) => o.image || o.imageUrl || o.mediaId)) {
    return true;
  }

  return false;
}

function getQuestionTextAlternatives(question: any): Record<string, string> {
  if (!question) return {};

  const result: Record<string, string> = {};

  // 1. Try question.alternatives
  if (question.alternatives && typeof question.alternatives === "object" && !Array.isArray(question.alternatives)) {
    Object.entries(question.alternatives).forEach(([key, val]) => {
      if (val) result[key] = String(val);
    });
  }

  // 2. Try question.options if it is a plain object
  if (Object.keys(result).length === 0 && question.options && typeof question.options === "object" && !Array.isArray(question.options)) {
    Object.entries(question.options).forEach(([key, val]) => {
      if (val) result[key] = String(val);
    });
  }

  // 3. Try question.options if it is an array
  if (Object.keys(result).length === 0 && question.options && Array.isArray(question.options)) {
    question.options.forEach((opt: any) => {
      if (opt && typeof opt === "object") {
        const label = opt.label || opt.id || opt.key;
        const text = opt.text || opt.value || opt.content;
        if (label && text) {
          result[label] = String(text);
        }
      }
    });
  }

  // 4. Try separated fields: optionA, optionB, optionC, optionD
  if (Object.keys(result).length === 0) {
    const labels = ["A", "B", "C", "D"] as const;
    labels.forEach(l => {
      const fieldVal = question[`option${l}`] || question[`alternative${l}`];
      if (fieldVal) {
        result[l] = String(fieldVal);
      }
    });
  }

  // 5. Fallback: labels with empty texts
  if (Object.keys(result).length === 0) {
    const labels = ["A", "B", "C", "D"] as const;
    labels.forEach(l => {
      result[l] = "";
    });
  }

  return result;
}

function getCOOptionImage(question: any, option: any, label: string): string {
  const normalizedLabel = String(label || option?.label || option?.id || option?.value || "").toUpperCase();

  return (
    option?.imageUrl ||
    option?.image ||
    option?.url ||
    option?.media?.image ||
    option?.metadata?.image ||

    question?.[`option${normalizedLabel}Image`] ||
    question?.[`option_${normalizedLabel.toLowerCase()}_image`] ||

    question?.media?.optionImages?.[normalizedLabel] ||
    question?.media?.images?.[normalizedLabel] ||

    question?.metadata?.optionImages?.[normalizedLabel] ||
    question?.metadata?.images?.[normalizedLabel] ||

    ""
  );
}

function safeParseArray(value: any): any[] {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export default function Questions() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  // Questions State
  const [questions, setQuestions] = useState<Question[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompetence, setSelectedCompetence] = useState<string | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string | null>(null);
  const [selectedSubtype, setSelectedSubtype] = useState<string | null>(null);

  // Selection State
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<string[]>([]);
  const [isBatchDeleteOpen, setIsBatchDeleteOpen] = useState(false);
  
  // Master checkbox ref for indeterminate state
  const masterCheckboxRef = useRef<HTMLInputElement>(null);

  // Modal States
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [activeQuestion, setActiveQuestion] = useState<Question | null>(null);

  // Toast State
  const [toast, setToast] = useState<{ visible: boolean; message: string; type: "success" | "error" }>({
    visible: false,
    message: "",
    type: "success"
  });

  // Resolved media URLs for viewing
  const [resolvedMediaUrls, setResolvedMediaUrls] = useState<Record<string, string>>({});


  // Resolve media for activeQuestion when viewed
  useEffect(() => {
    let active = true;
    const urlsToRevoke: string[] = [];

    const resolveMedia = async () => {
      if (!activeQuestion) {
        setResolvedMediaUrls({});
        return;
      }

      const resolved: Record<string, string> = {};

      // 1. Resolve imageMediaId
      if (activeQuestion.imageMediaId) {
        const url = await mediaService.getMediaUrl(activeQuestion.imageMediaId);
        if (url && active) {
          resolved[activeQuestion.imageMediaId] = url;
          urlsToRevoke.push(url);
        }
      }

      // 2. Resolve audioMediaId
      if (activeQuestion.audioMediaId) {
        const url = await mediaService.getMediaUrl(activeQuestion.audioMediaId);
        if (url && active) {
          resolved[activeQuestion.audioMediaId] = url;
          urlsToRevoke.push(url);
        }
      }

      // 3. Resolve options mediaIds
      if (activeQuestion.options) {
        for (const opt of activeQuestion.options) {
          if (opt.mediaId) {
            const url = await mediaService.getMediaUrl(opt.mediaId);
            if (url && active) {
              resolved[opt.mediaId] = url;
              urlsToRevoke.push(url);
            }
          }
        }
      }

      if (active) {
        setResolvedMediaUrls(resolved);
      }
    };

    resolveMedia();

    return () => {
      active = false;
      urlsToRevoke.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          console.warn("Error revoking object URL:", e);
        }
      });
    };
  }, [activeQuestion]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLocalQuestions, setHasLocalQuestions] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);

  // Load questions from service on mount
  useEffect(() => {
    loadQuestions();
    checkLocalQuestions();
  }, []);

  const checkLocalQuestions = () => {
    const local = localStorage.getItem("tef_questions");
    if (local) {
      try {
        const parsed = JSON.parse(local);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setHasLocalQuestions(true);
          return;
        }
      } catch (e) {}
    }
    setHasLocalQuestions(false);
  };

  const loadQuestions = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await questionService.getQuestions();
      setQuestions(list);
      
      // Diagnóstico temporário
      console.table(list.map(q => ({
        id: q.id,
        title: q.title,
        competence: q.competence,
        section: q.section
      })));

      // [QUESTION_BANK_CO_MEDIA_DEBUG]
      try {
        const listeningQuestions = list.filter((q: any) => {
          const comp = String(q.competency || q.competence || "").toLowerCase();
          return comp === "listening" || comp === "co" || comp.includes("orale") || comp.includes("listening");
        });
        console.log("[QUESTION_BANK_CO_MEDIA_DEBUG] Loaded CO questions from bank:", listeningQuestions.map((q: any) => ({
          id: q.id,
          title: q.title,
          section: q.section,
          audioUrl: getQuestionAudioUrl(q),
          optionAImage: getOptionImageUrl(q, "A"),
          optionBImage: getOptionImageUrl(q, "B"),
          optionCImage: getOptionImageUrl(q, "C"),
          optionDImage: getOptionImageUrl(q, "D"),
          optionsCount: q.options?.length
        })));
      } catch (errDebug) {
        console.error("[QUESTION_BANK_CO_MEDIA_DEBUG] Error logging CO questions:", errDebug);
      }
    } catch (err: any) {
      console.error(err);
      setError("Falha ao carregar as questões. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleMigrateQuestions = async () => {
    if (!isAdmin) {
      showToast("Você não tem permissão para modificar o banco de questões.", "error");
      return;
    }
    setIsMigrating(true);
    try {
      const res = await questionService.migrateLocalQuestionsToSupabase();
      showToast(
        `Migração concluída! Sucesso: ${res.successCount}, Duplicados: ${res.duplicateCount}, Erros: ${res.errorCount}.`,
        res.errorCount > 0 ? "error" : "success"
      );
      if (res.successCount > 0) {
        localStorage.removeItem("tef_questions");
        setHasLocalQuestions(false);
      }
      loadQuestions();
    } catch (err: any) {
      console.error(err);
      showToast(err.message || "Falha ao migrar questões locais.", "error");
    } finally {
      setIsMigrating(false);
    }
  };

  // Handle incoming React Router State (for Save/Update Toast feedbacks)
  useEffect(() => {
    if (location.state?.toastMessage) {
      showToast(location.state.toastMessage, "success");
      // Clear navigation state to prevent re-triggering on manual refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Toast trigger utility
  const showToast = (message: string, type: "success" | "error" = "success") => {
    setToast({ visible: true, message, type });
  };

  // Auto-hide Toast after 3 seconds
  useEffect(() => {
    if (toast.visible) {
      const timer = setTimeout(() => {
        setToast(prev => ({ ...prev, visible: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.visible]);

  // Actions
  const handleViewDetails = (q: Question) => {
    setActiveQuestion(q);
    setIsViewOpen(true);
    
    // Log active question details and resolved images
    const opts = safeParseArray(q.options);
    const optionA = opts.find((o: any) => (o.label || o.id) === "A");
    const optionB = opts.find((o: any) => (o.label || o.id) === "B");
    const optionC = opts.find((o: any) => (o.label || o.id) === "C");
    const optionD = opts.find((o: any) => (o.label || o.id) === "D");
    
    console.log("[QUESTION_PREVIEW_MEDIA_DEBUG]", {
      id: q.id,
      title: q.title,
      options: q.options,
      optionAImage: (q as any).optionAImage,
      optionBImage: (q as any).optionBImage,
      optionCImage: (q as any).optionCImage,
      optionDImage: (q as any).optionDImage,
      media: q.media,
      metadata: q.metadata,
      resolvedImages: {
        A: getCOOptionImage(q, optionA, "A"),
        B: getCOOptionImage(q, optionB, "B"),
        C: getCOOptionImage(q, optionC, "C"),
        D: getCOOptionImage(q, optionD, "D")
      }
    });
  };

  const handleDeleteTrigger = (q: Question) => {
    setActiveQuestion(q);
    setIsDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!isAdmin) {
      showToast("Você não tem permissão para modificar o banco de questões.", "error");
      setIsDeleteOpen(false);
      setActiveQuestion(null);
      return;
    }
    if (activeQuestion) {
      const success = await questionService.deleteQuestion(activeQuestion.id);
      if (success) {
        loadQuestions();
        showToast("Questão excluída com sucesso.");
      } else {
        showToast("Erro ao tentar excluir a questão.", "error");
      }
      setIsDeleteOpen(false);
      setActiveQuestion(null);
    }
  };

  // Filtering Logic
  const filteredQuestions = questions.filter(q => {
    const matchesSearch = q.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          q.section.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCompetence = selectedCompetence ? q.competence === selectedCompetence : true;
    const matchesDifficulty = selectedDifficulty ? q.difficulty === selectedDifficulty : true;
    const matchesSubtype = selectedSubtype ? (q.subSection === selectedSubtype || q.sub_section === selectedSubtype || q.metadata?.subSection === selectedSubtype) : true;

    return matchesSearch && matchesCompetence && matchesDifficulty && matchesSubtype;
  });

  // Selection Handlers
  const toggleQuestionSelection = (questionId: string) => {
    setSelectedQuestionIds(prev =>
      prev.includes(questionId)
        ? prev.filter(id => id !== questionId)
        : [...prev, questionId]
    );
  };

  const visibleQuestionIds = filteredQuestions.map(q => q.id);
  const isEveryVisibleSelected = visibleQuestionIds.length > 0 && visibleQuestionIds.every(id => selectedQuestionIds.includes(id));
  const isSomeVisibleSelected = visibleQuestionIds.length > 0 && visibleQuestionIds.some(id => selectedQuestionIds.includes(id)) && !isEveryVisibleSelected;

  useEffect(() => {
    if (masterCheckboxRef.current) {
      masterCheckboxRef.current.indeterminate = isSomeVisibleSelected;
    }
  }, [isSomeVisibleSelected]);

  const handleSelectAllToggle = () => {
    if (isEveryVisibleSelected) {
      // Remove all visible from selection
      setSelectedQuestionIds(prev => prev.filter(id => !visibleQuestionIds.includes(id)));
    } else {
      // Add all visible to selection
      setSelectedQuestionIds(prev => {
        const next = [...prev];
        visibleQuestionIds.forEach(id => {
          if (!next.includes(id)) {
            next.push(id);
          }
        });
        return next;
      });
    }
  };

  const handleClearSelection = () => {
    setSelectedQuestionIds([]);
  };

  const handleBatchDeleteTrigger = () => {
    setIsBatchDeleteOpen(true);
  };

  const handleBatchDeleteConfirm = async () => {
    if (!isAdmin) {
      showToast("Você não tem permissão para modificar o banco de questões.", "error");
      setIsBatchDeleteOpen(false);
      return;
    }
    const success = await questionService.deleteQuestions(selectedQuestionIds);
    if (success) {
      loadQuestions();
      showToast(`${selectedQuestionIds.length} ${selectedQuestionIds.length === 1 ? "questão excluída" : "questões excluídas"} com sucesso.`);
      setSelectedQuestionIds([]);
    } else {
      showToast("Erro ao tentar excluir as questões.", "error");
    }
    setIsBatchDeleteOpen(false);
  };

  const stats = useMemo(() => {
    let total = 0;
    let reading = 0;
    let listening = 0;
    let writing = 0;

    questions.forEach(q => {
      total++;
      const comp = normalizeCompetency(q.competence ?? (q as any).competency ?? (q as any).skill ?? q.type);
      if (comp === "reading") reading++;
      else if (comp === "listening") listening++;
      else if (comp === "writing") writing++;
    });

    return { total, reading, listening, writing };
  }, [questions]);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate(-1)}
        className="text-gray-500 hover:text-gray-700 hover:bg-gray-100/50 -ml-2 rounded-lg gap-1.5 font-semibold text-xs self-start"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("common.back", "Voltar")}
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">{t("questionsBank.title", "Banco de Questões")}</h1>
          <p className="text-gray-500 mt-1">{t("questionsBank.subtitle", "Gerencie e organize todas as questões da plataforma.")}</p>
        </div>
        <div className="flex items-center gap-2">
          {questionService.isSupabaseConfigured && hasLocalQuestions && (
            <Button
              onClick={handleMigrateQuestions}
              disabled={isMigrating}
              variant="outline"
              className="border-amber-200 bg-amber-50/50 text-amber-700 hover:bg-amber-100 hover:text-amber-800 rounded-xl gap-1.5"
            >
              <Database className="w-4 h-4" />
              {isMigrating ? t("questionsBank.migrating", "Migrando...") : t("questionsBank.migrateLocal", "Migrar Questões Locais")}
            </Button>
          )}

          <Button asChild className="bg-primary hover:bg-primary/90 text-white shadow-md rounded-xl">
            <Link to="/questions/new">
              <Plus className="w-4 h-4 mr-2" />
              {t("questionsBank.newQuestion", "Nova Questão")}
            </Link>
          </Button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 w-full">
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-between">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider block mb-1">{t("questionsBank.totalQuestions", "Total de Questões")}</span>
          <span className="text-3xl font-black text-gray-900 font-mono">{stats.total}</span>
        </div>
        <div className="bg-purple-50/60 p-5 rounded-2xl shadow-sm border border-purple-100 flex flex-col justify-between">
          <span className="text-xs font-bold text-purple-600 uppercase tracking-wider block mb-1">Compréhension Écrite (CE)</span>
          <span className="text-3xl font-black text-purple-950 font-mono">{stats.reading}</span>
        </div>
        <div className="bg-blue-50/60 p-5 rounded-2xl shadow-sm border border-blue-100 flex flex-col justify-between">
          <span className="text-xs font-bold text-blue-600 uppercase tracking-wider block mb-1">Compréhension Orale (CO)</span>
          <span className="text-3xl font-black text-blue-950 font-mono">{stats.listening}</span>
        </div>
        <div className="bg-emerald-50/60 p-5 rounded-2xl shadow-sm border border-emerald-100 flex flex-col justify-between">
          <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider block mb-1">Expression Écrite (EE)</span>
          <span className="text-3xl font-black text-emerald-950 font-mono">{stats.writing}</span>
        </div>
      </div>

      {/* Filters Area */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4 items-center">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder={t("questionsBank.searchPlaceholder", "Buscar por título ou seção...")} 
            className="pl-9 bg-gray-50 border-transparent focus-visible:ring-primary/20 focus-visible:border-primary w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          {/* Competence Filter Quick Toggles */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className={`rounded-lg bg-white hover:bg-gray-50 flex-1 md:flex-none ${selectedCompetence ? "border-primary text-primary" : "text-gray-600"}`}>
                <Filter className="w-4 h-4 mr-2" />
                {selectedCompetence ? `Comp: ${getCompetencyLabel(selectedCompetence)}` : t("questionsBank.competence", "Competência")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[185px]">
              <DropdownMenuItem onClick={() => setSelectedCompetence(null)} className="font-medium">
                {t("questionsBank.allCompetences", "Todas Competências")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSelectedCompetence("Reading")}>
                Compréhension Écrite (CE)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSelectedCompetence("Listening")}>
                Compréhension Orale (CO)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSelectedCompetence("Writing")}>
                Expression Écrite (EE)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Difficulty Filter Quick Toggles */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className={`rounded-lg bg-white hover:bg-gray-50 flex-1 md:flex-none ${selectedDifficulty ? "border-primary text-primary" : "text-gray-600"}`}>
                <Filter className="w-4 h-4 mr-2" />
                {selectedDifficulty ? `Dif: ${
                  selectedDifficulty === 'Fácil' ? t("questionsBank.easy", "Fácil") : 
                  selectedDifficulty === 'Médio' ? t("questionsBank.medium", "Médio") : 
                  t("questionsBank.hard", "Difícil")
                }` : t("questionsBank.difficulty", "Dificuldade")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[180px]">
              <DropdownMenuItem onClick={() => setSelectedDifficulty(null)} className="font-medium">
                {t("questionsBank.allDifficulties", "Todas Dificuldades")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSelectedDifficulty("Fácil")}>
                {t("questionsBank.easy", "Fácil")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSelectedDifficulty("Médio")}>
                {t("questionsBank.medium", "Médio")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSelectedDifficulty("Difícil")}>
                {t("questionsBank.hard", "Difícil")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Subtype Filter Quick Toggles for Reading */}
          {selectedCompetence === "Reading" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className={`rounded-lg bg-white hover:bg-gray-50 flex-1 md:flex-none ${selectedSubtype ? "border-purple-600 text-purple-600" : "text-gray-600"}`}>
                  <Filter className="w-4 h-4 mr-2" />
                  {selectedSubtype === "lecture_rapide_textes" ? "Textes" :
                   selectedSubtype === "lecture_rapide_graphiques" ? "Graphiques" :
                   selectedSubtype === "phrases_lacunaires" ? "Phrases lac." :
                   selectedSubtype === "textes_lacunaires" ? "Textes lac." : t("questionsBank.subtype", "Subtipo")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[220px]">
                <DropdownMenuItem onClick={() => setSelectedSubtype(null)} className="font-medium">
                  {t("questionsBank.allSubtypes", "Todos Subtipos")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedSubtype("lecture_rapide_textes")}>
                  Lecture rapide de textes
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedSubtype("lecture_rapide_graphiques")}>
                  Lecture rapide de graphiques
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedSubtype("phrases_lacunaires")}>
                  Phrases lacunaires
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSelectedSubtype("textes_lacunaires")}>
                  Textes lacunaires
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Batch Action Bar */}
      {selectedQuestionIds.length > 0 && (
        <div className="bg-purple-50/80 border border-purple-100 p-4 rounded-xl shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4 animate-in slide-in-from-top-4 fade-in duration-300">
          <div className="flex items-center gap-2">
            <Badge className="bg-primary hover:bg-primary/90 text-white rounded-full">
              {selectedQuestionIds.length}
            </Badge>
            <span className="text-sm font-semibold text-gray-700">
              {selectedQuestionIds.length === 1 ? t("questionsBank.questionSelected", "questão selecionada") : t("questionsBank.questionsSelected", "questões selecionadas")}
            </span>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button 
              variant="outline" 
              onClick={handleClearSelection}
              className="flex-1 sm:flex-none rounded-lg border-gray-200 text-gray-600 hover:bg-gray-100 text-sm font-medium"
            >
              {t("questionsBank.clearSelection", "Limpar seleção")}
            </Button>
            <Button 
              onClick={handleBatchDeleteTrigger}
              className="flex-1 sm:flex-none bg-red-600 hover:bg-red-700 text-white rounded-lg shadow-md text-sm font-medium gap-2 animate-in fade-in"
            >
              <Trash2 className="w-4 h-4" />
              {t("questionsBank.deleteSelected", "Excluir selecionadas")}
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-24 space-y-4">
            <div className="w-10 h-10 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin" />
            <p className="text-sm font-medium text-gray-500 animate-pulse">{t("questionsBank.loadingDatabase", "Carregando banco de questões do Supabase...")}</p>
          </div>
        ) : error ? (
          <div className="p-12 text-center text-red-600">
            <AlertTriangle className="w-10 h-10 mx-auto text-red-400 mb-2" />
            <p className="font-semibold">{error}</p>
            <Button onClick={loadQuestions} size="sm" variant="outline" className="mt-4 rounded-xl">
              {t("common.tryAgain", "Tentar Novamente")}
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto w-full">
            <Table>
              <TableHeader className="bg-gray-50">
                <TableRow>
                  <TableHead className="w-[50px] text-center pl-4">
                    <input 
                      ref={masterCheckboxRef}
                      type="checkbox"
                      className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4 cursor-pointer accent-purple-600"
                      checked={visibleQuestionIds.length > 0 && visibleQuestionIds.every(id => selectedQuestionIds.includes(id))}
                      onChange={handleSelectAllToggle}
                      disabled={filteredQuestions.length === 0}
                    />
                  </TableHead>
                  <TableHead className="font-semibold text-gray-600">{t("questionsBank.titleCol", "Título")}</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t("questionsBank.competence", "Competência")}</TableHead>
                  <TableHead className="font-semibold text-gray-600 hidden md:table-cell">{t("questionsBank.section", "Seção")}</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t("questionsBank.difficulty", "Dificuldade")}</TableHead>
                  <TableHead className="font-semibold text-gray-600 text-center">{t("questionsBank.media", "Mídia")}</TableHead>
                  <TableHead className="text-right font-semibold text-gray-600 pr-4">{t("questionsBank.actions", "Ações")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredQuestions.map((q) => (
                  <TableRow key={q.id} className="hover:bg-gray-50/50 transition-colors">
                    <TableCell className="text-center w-[50px] pl-4">
                      <input 
                        type="checkbox"
                        className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4 cursor-pointer accent-purple-600"
                        checked={selectedQuestionIds.includes(q.id)}
                        onChange={() => toggleQuestionSelection(q.id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium text-gray-900">{q.title}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={
                        q.competence === 'Reading' ? 'bg-purple-100 text-purple-700 hover:bg-purple-100' :
                        q.competence === 'Listening' ? 'bg-blue-100 text-blue-700 hover:bg-blue-100' :
                        'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                      }>
                        {getCompetencyLabel(q.competence)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-500 hidden md:table-cell">
                      {normalizeQuestionCompetency(q.competence) === "Listening" ? normalizeListeningSection(q.section) : q.section}
                      {q.subSection ? (
                        <span className="block text-[10px] text-purple-600 font-bold">
                          ({q.subSection === "phrases_lacunaires" ? "Phrases lacunaires" :
                            q.subSection === "textes_lacunaires" ? "Textes lacunaires" :
                            q.subSection === "lecture_rapide_textes" ? "Lecture rapide de textes" :
                            "Lecture rapide de graphiques"})
                        </span>
                      ) : (q.section === "Phrases lacunaires / Textes lacunaires" || q.section === "Phrases et textes lacunaires" ? (
                        <span className="block text-[10px] text-amber-600 font-bold">
                          {t("questionsBank.subtypeUndefined", "(Subtipo não definido)")}
                        </span>
                      ) : null)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className={`text-sm font-medium ${
                          (q as any).difficultyLabel
                            ? "text-indigo-700"
                            : q.difficulty === 'Fácil' ? 'text-green-600' :
                              q.difficulty === 'Médio' ? 'text-amber-600' :
                              'text-red-600'
                        }`}>
                          {
                            (q as any).difficultyLabel || (
                              q.difficulty === 'Fácil' ? t("questionsBank.easy", "Fácil") :
                              q.difficulty === 'Médio' ? t("questionsBank.medium", "Médio") :
                              q.difficulty === 'Difícil' ? t("questionsBank.hard", "Difícil") : q.difficulty
                            )
                          }
                        </span>
                        {(q as any).probableLevel && (
                          <span className="text-[10px] text-indigo-400 font-medium leading-tight">
                            {(q as any).probableLevel}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-center gap-2">
                        {q.hasImage ? <span title={t("questionsBank.hasImage", "Possui Imagem")}><ImageIcon className="w-4.5 h-4.5 text-blue-500 bg-blue-50 p-0.5 rounded" /></span> : <div className="w-4.5 h-4.5" />}
                        {(q.hasAudio || Boolean(getQuestionAudioUrl(q))) ? <span title={t("questionsBank.hasAudio", "Possui Áudio")}><Headphones className="w-4.5 h-4.5 text-purple-500 bg-purple-50 p-0.5 rounded" /></span> : <div className="w-4.5 h-4.5" />}
                      </div>
                    </TableCell>
                    <TableCell className="text-right pr-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-gray-100 rounded-full">
                            <span className="sr-only">{t("questionsBank.openMenu", "Abrir menu")}</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[180px] rounded-xl shadow-lg border border-gray-100 p-1 bg-white">
                          <DropdownMenuItem onClick={() => handleViewDetails(q)} className="cursor-pointer rounded-lg hover:bg-gray-50 gap-2 font-medium text-gray-700">
                            <Eye className="w-4 h-4 text-gray-400" /> {t("questionsBank.viewQuestion", "Visualizar")}
                          </DropdownMenuItem>
                          <DropdownMenuItem asChild className="cursor-pointer rounded-lg hover:bg-gray-50 gap-2 font-medium text-gray-700">
                            <Link to={`/questions/${q.id}/edit`}>
                              <Edit2 className="w-4 h-4 text-gray-400" /> {t("questionsBank.edit", "Editar")}
                            </Link>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDeleteTrigger(q)} className="cursor-pointer rounded-lg text-red-600 focus:text-red-600 focus:bg-red-50 gap-2 font-medium">
                            <Trash2 className="w-4 h-4 text-red-400" /> {t("questionsBank.delete", "Excluir")}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredQuestions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="h-28 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center gap-1.5 py-4">
                        <span className="text-lg font-medium text-gray-700">{t("questionsBank.noQuestionsFound", "Nenhuma questão encontrada")}</span>
                        <span className="text-sm text-gray-400">{t("questionsBank.tryDifferentTerms", "Tente buscar por termos diferentes ou ajuste os filtros.")}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* --- MODAL DE VISUALIZAÇÃO CONTEXTUAL --- */}
      <ContextModal 
        isOpen={isViewOpen} 
        onClose={() => setIsViewOpen(false)}
        size="2xl"
      >
        {activeQuestion && (
          <div className="space-y-6">
            <div className="border-b border-gray-100 pb-4">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={
                  activeQuestion.competence === 'Reading' ? 'bg-purple-100 text-purple-700 border-none hover:bg-purple-100' :
                  activeQuestion.competence === 'Listening' ? 'bg-blue-100 text-blue-700 border-none hover:bg-blue-100' :
                  'bg-emerald-100 text-emerald-700 border-none hover:bg-emerald-100'
                }>
                  {getCompetencyLabel(activeQuestion.competence)}
                </Badge>
                <Badge variant="outline" className="border-gray-200 text-gray-500 text-xs">
                  {activeQuestion.section}
                  {activeQuestion.subSection ? ` · ${
                    activeQuestion.subSection === "phrases_lacunaires" ? "Phrases lacunaires" :
                    activeQuestion.subSection === "textes_lacunaires" ? "Textes lacunaires" :
                    activeQuestion.subSection === "lecture_rapide_textes" ? "Lecture rapide de textes" :
                    "Lecture rapide de graphiques"
                  }` : (activeQuestion.section === "Phrases lacunaires / Textes lacunaires" || activeQuestion.section === "Phrases et textes lacunaires" ? " · Subtipo não definido" : "")}
                </Badge>
                <Badge variant="outline" className={
                  (activeQuestion as any).difficultyLabel
                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
                    : activeQuestion.difficulty === 'Fácil' ? 'bg-green-50 border-green-200 text-green-700' :
                      activeQuestion.difficulty === 'Médio' ? 'bg-amber-50 border-amber-200 text-amber-700' :
                      'bg-red-50 border-red-200 text-red-700'
                }>
                  {(activeQuestion as any).difficultyLabel
                    ? `${(activeQuestion as any).difficultyLabel}${(activeQuestion as any).probableLevel ? ` · ${(activeQuestion as any).probableLevel}` : ""}`
                    : activeQuestion.difficulty}
                </Badge>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 leading-tight">
                {activeQuestion.title}
              </h3>
              {activeQuestion.createdAt && (
                <p className="flex items-center gap-1 text-xs text-gray-400 mt-1.5">
                  <Calendar className="w-3.5 h-3.5" /> Criada em: {activeQuestion.createdAt}
                </p>
              )}
            </div>

            {/* Main Content Fields */}
            <div className="space-y-4">
              {/* Command/Instruction */}
              {activeQuestion.instruction && (
                <div className="bg-blue-50/50 border-l-4 border-blue-500 p-3.5 rounded-r-xl">
                  <span className="text-xs font-semibold text-blue-800 uppercase tracking-wider block mb-1">Enunciado / Instrução</span>
                  <p className="text-sm font-medium text-gray-700">{activeQuestion.instruction}</p>
                </div>
              )}

              {/* Main Text Content */}
              {activeQuestion.text && (
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1.5">Texto de Suporte</span>
                  <p className="text-gray-800 text-sm whitespace-pre-wrap leading-relaxed italic">{activeQuestion.text}</p>
                </div>
              )}
              {/* Image Placeholder if hasImage */}
              {activeQuestion.hasImage && (
                <div className="rounded-xl overflow-hidden border border-gray-100 shadow-sm bg-gray-50 max-h-[220px] flex items-center justify-center">
                  {activeQuestion.imageMediaId && resolvedMediaUrls[activeQuestion.imageMediaId] ? (
                    <img src={resolvedMediaUrls[activeQuestion.imageMediaId]} alt={activeQuestion.title} className="w-full h-full object-cover" />
                  ) : activeQuestion.imageUrl ? (
                    <img src={activeQuestion.imageUrl} alt={activeQuestion.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="py-8 flex flex-col items-center justify-center text-gray-400 gap-1.5">
                      <ImageIcon className="w-8 h-8 text-gray-300" />
                      <span className="text-xs font-medium">Imagem Ilustrativa (Simulada)</span>
                    </div>
                  )}
                </div>
              )}

              {/* Audio Player if Listening */}
              {activeQuestion.competence === "Listening" && activeQuestion.hasAudio && (
                <div className="bg-purple-50/50 p-3.5 rounded-xl border border-purple-100 flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-purple-700">
                    <Volume2 className="w-4 h-4" />
                    <span className="text-xs font-semibold uppercase tracking-wider">Gravação de Áudio</span>
                  </div>
                  {activeQuestion.audioMediaId && resolvedMediaUrls[activeQuestion.audioMediaId] ? (
                    <AudioPlayerWithSpeed src={resolvedMediaUrls[activeQuestion.audioMediaId]} className="w-full mt-1" compact />
                  ) : activeQuestion.audioUrl ? (
                    <AudioPlayerWithSpeed src={activeQuestion.audioUrl} className="w-full mt-1" compact />
                  ) : (
                    <div className="py-2 text-center text-xs text-purple-500 font-medium">
                      Player de Áudio Simulado para Listening
                    </div>
                  )}
                </div>
              )}

              {/* Alternatives List (Text or Images) */}
              {((activeQuestion.type as any) === "mcq" || (activeQuestion.type as any) === "multiple_choice") && (() => {
                let parsedOptions = safeParseArray(activeQuestion.options);
                if (parsedOptions.length === 0) {
                  const labels = ["A", "B", "C", "D"] as const;
                  const altMap = getQuestionTextAlternatives(activeQuestion);
                  parsedOptions = labels.map(l => ({
                    label: l,
                    text: altMap[l] || ""
                  }));
                }

                return shouldRenderImageOptions(activeQuestion) ? (
                  // Image Options List (Listening Section A)
                  <div className="space-y-3">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">Alternativas (Imagens)</span>
                    <div className="grid grid-cols-2 gap-4">
                      {parsedOptions.map((opt: any) => {
                        const isCorrect = opt.label === activeQuestion.correctAnswer;
                        
                        // Resolve the option image using getCOOptionImage
                        const rawOptImg = getCOOptionImage(activeQuestion, opt, opt.label);
                        const finalOptImg = (opt.mediaId && resolvedMediaUrls[opt.mediaId]) || rawOptImg || opt.imageUrl || "";

                        return (
                          <div 
                            key={opt.label} 
                            className={`relative overflow-hidden rounded-xl border-2 p-1 transition-all aspect-[4/3] flex items-center justify-center ${
                              isCorrect 
                                ? "border-emerald-500 ring-2 ring-emerald-500/20 bg-emerald-50/10" 
                                : "border-gray-100 bg-white"
                            }`}
                          >
                            {finalOptImg ? (
                              <img 
                                src={finalOptImg} 
                                alt={`Alternativa ${opt.label}`} 
                                className="max-w-full h-full object-contain rounded-lg" 
                                onError={(e) => {
                                  // Add fallback of error: show "Imagem não carregou" and log
                                  console.warn("[QUESTION_PREVIEW_IMAGE_LOAD_ERROR]", {
                                    questionId: activeQuestion.id,
                                    label: opt.label,
                                    imageUrl: finalOptImg,
                                  });
                                  const imgEl = e.currentTarget as HTMLImageElement;
                                  const parent = imgEl.parentElement;
                                  if (parent) {
                                    // Replace the image with error text safely
                                    imgEl.style.display = "none";
                                    const errorSpan = document.createElement("span");
                                    errorSpan.className = "text-red-500 text-xs font-medium";
                                    errorSpan.innerText = "Imagem não carregou";
                                    parent.appendChild(errorSpan);
                                  }
                                }}
                              />
                            ) : (
                              <div className="text-gray-400 text-xs">Sem imagem</div>
                            )}
                            <div className={`absolute top-2 left-2 w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs shadow-sm ${
                              isCorrect ? "bg-emerald-500 text-white" : "bg-white/80 backdrop-blur border border-gray-200 text-gray-600"
                            }`}>
                              {opt.label}
                            </div>
                            {isCorrect && (
                              <div className="absolute top-2 right-2 bg-emerald-500 text-white rounded-full p-0.5 shadow">
                                <Check className="w-3.5 h-3.5" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  // Textual Alternatives List (Default for CE and all other CO sections)
                  <div className="space-y-2.5">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">Alternativas</span>
                    {Object.entries(getQuestionTextAlternatives(activeQuestion)).map(([key, value]) => {
                      const isCorrect = key === activeQuestion.correctAnswer;
                      return (
                        <div 
                          key={key} 
                          className={`flex gap-3 p-3 rounded-xl border items-center transition-colors ${
                            isCorrect 
                              ? "bg-emerald-50 border-emerald-200 text-emerald-900" 
                              : "bg-white border-gray-100 text-gray-700 hover:bg-gray-50/50"
                          }`}
                        >
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border-2 ${
                            isCorrect 
                              ? "bg-emerald-500 border-emerald-500 text-white" 
                              : "bg-gray-50 border-gray-200 text-gray-400"
                          }`}>
                            {key}
                          </div>
                          <span className="text-sm font-medium flex-1">{value}</span>
                          {isCorrect && <Check className="w-4.5 h-4.5 text-emerald-600" />}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Correct Answer Explanation */}
              {(((activeQuestion.type as any) === "mcq" || (activeQuestion.type as any) === "multiple_choice") && activeQuestion.explanation) && (
                <div className="bg-emerald-50/20 border border-emerald-100 p-4 rounded-xl space-y-1">
                  <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wider block">Gabarito Comentado</span>
                  <p className="text-gray-700 text-sm leading-relaxed">{activeQuestion.explanation}</p>
                </div>
              )}

              {/* Explanation for Written/Essay */}
              {activeQuestion.type === "written" && activeQuestion.explanation && (
                <div className="bg-blue-50/20 border border-blue-100 p-4 rounded-xl space-y-1">
                  <span className="text-xs font-semibold text-blue-800 uppercase tracking-wider block">Dicas de Correção / Instruções</span>
                  <p className="text-gray-700 text-sm leading-relaxed">{activeQuestion.explanation}</p>
                </div>
              )}

              {/* Tags */}
              {activeQuestion.tags && activeQuestion.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-2">
                  <Tag className="w-3.5 h-3.5 text-gray-400 mr-1 self-center" />
                  {activeQuestion.tags.map((tag, idx) => (
                    <Badge key={idx} variant="secondary" className="bg-gray-100 text-gray-600 hover:bg-gray-100 rounded-lg text-xs font-normal">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-gray-100 pt-4 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setIsViewOpen(false)} className="rounded-xl border-gray-200 text-gray-600 hover:bg-gray-50">
                Fechar
              </Button>
              <Button asChild className="bg-primary hover:bg-primary/90 text-white rounded-xl shadow-md">
                <Link to={`/questions/${activeQuestion.id}/edit`}>
                  <Edit2 className="w-4 h-4 mr-2" /> Editar Questão
                </Link>
              </Button>
            </div>
          </div>
        )}
      </ContextModal>

      {/* --- MODAL DE CONFIRMAÇÃO DE EXCLUSÃO --- */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="max-w-md rounded-2xl bg-white p-6 shadow-2xl border-none">
          {activeQuestion && (
            <div className="space-y-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-600 mb-2">
                <Trash2 className="w-5 h-5" />
              </div>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold text-gray-900 leading-tight">
                  Excluir Questão
                </DialogTitle>
                <div className="text-gray-500 text-sm mt-1.5 space-y-2">
                  <p>
                    Tem certeza que deseja excluir esta questão? Esta ação é permanente e não poderá ser desfeita.
                  </p>
                  <div className="p-3 bg-red-50/50 border border-red-100 rounded-xl">
                    <span className="text-xs font-semibold text-red-700 block mb-0.5">Questão selecionada:</span>
                    <span className="font-semibold text-gray-800 text-sm">{activeQuestion.title}</span>
                  </div>
                </div>
              </DialogHeader>

              <DialogFooter className="pt-2 flex gap-2">
                <Button variant="outline" onClick={() => setIsDeleteOpen(false)} className="flex-1 rounded-xl border-gray-200 text-gray-600 hover:bg-gray-50">
                  Cancelar
                </Button>
                <Button onClick={handleDeleteConfirm} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-md">
                  Excluir definitivamente
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* --- MODAL DE CONFIRMAÇÃO DE EXCLUSÃO EM LOTE --- */}
      <Dialog open={isBatchDeleteOpen} onOpenChange={setIsBatchDeleteOpen}>
        <DialogContent className="max-w-md rounded-2xl bg-white p-6 shadow-2xl border-none">
          <div className="space-y-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-red-600 mb-2">
              <Trash2 className="w-5 h-5" />
            </div>
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-gray-900 leading-tight">
                Excluir Questões em Lote
              </DialogTitle>
              <div className="text-gray-500 text-sm mt-1.5 space-y-2">
                <p>
                  Tem certeza que deseja excluir as {selectedQuestionIds.length} questões selecionadas? Esta ação é permanente e não poderá ser desfeita.
                </p>
              </div>
            </DialogHeader>

            <DialogFooter className="pt-2 flex gap-2">
              <Button variant="outline" onClick={() => setIsBatchDeleteOpen(false)} className="flex-1 rounded-xl border-gray-200 text-gray-600 hover:bg-gray-50">
                Cancelar
              </Button>
              <Button onClick={handleBatchDeleteConfirm} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-md">
                Excluir definitivamente
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* --- TOAST DE NOTIFICAÇÃO PREMIUM --- */}
      {toast.visible && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-gray-900 text-white px-5 py-3.5 rounded-2xl shadow-2xl border border-gray-800 animate-in slide-in-from-bottom-5 fade-in duration-300">
          <CheckCircle className="w-5 h-5 text-green-400" />
          <span className="text-sm font-semibold">{toast.message}</span>
          <button 
            onClick={() => setToast(prev => ({ ...prev, visible: false }))}
            className="ml-2 hover:bg-gray-800 p-1 rounded-full text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
